'use client'

import { useEffect } from 'react'
import { useCookieConsent } from '@/hooks/use-cookie-consent'
import { useGoogleConsentMode } from '@/hooks/use-google-consent-mode'
import { initPostHog, shouldForcePostHog } from '@/instrumentation-client'
import { CookieConsentBanner } from '@/components/cookie-consent-banner'
import { useCapacitorBridge } from '@/components/capacitor-bridge-provider'

/**
 * Provider que gestiona el consentimiento de cookies y la inicialización de analytics
 * - PostHog: se inicializa solo después de consentimiento (o siempre si FORCE_ENABLE está activo)
 * - Google Ads: se carga siempre con Consent Mode v2 (default: denied)
 * - En native app (iOS/Android): se deshabilita tracking publicitario client-side
 *   para cumplir con App Store Guideline 5.1.2(i) (ATT)
 *
 * ⚠️ TEMPORAL: Si NEXT_PUBLIC_POSTHOG_FORCE_ENABLE=true, PostHog se inicializa siempre
 * sin requerir consentimiento. Esto NO cumple con GDPR. Usar solo temporalmente.
 *
 * Debe ser usado dentro de un Client Component
 */
export function CookieConsentProvider() {
  const { hasAnalyticsConsent, isLoading } = useCookieConsent()
  const forceEnabled = shouldForcePostHog()
  const { isNativeApp } = useCapacitorBridge()

  // Google Ads con Consent Mode (siempre, pero con default denied); skip en native app
  useGoogleConsentMode()

  // En native app, inicializar PostHog como analytics first-party sin banner/cookies.
  useEffect(() => {
    if (!isNativeApp) return
    initPostHog({ mode: 'native' })
  }, [isNativeApp])

  // Inicializar PostHog cuando hay consentimiento O si está forzado temporalmente
  // Skip en native app porque se inicializa con configuración propia arriba.
  useEffect(() => {
    if (isNativeApp || isLoading) return

    // ⚠️ TEMPORAL: Si FORCE_ENABLE está activo, inicializar siempre
    if (forceEnabled) {
      initPostHog()
      return
    }

    // Comportamiento normal: solo con consentimiento
    if (hasAnalyticsConsent) {
      initPostHog()
    }
  }, [hasAnalyticsConsent, isLoading, forceEnabled, isNativeApp])

  // En native app, no mostrar banner de cookies
  if (isNativeApp) {
    return null
  }

  return <CookieConsentBanner />
}

export default CookieConsentProvider
